<template>
  <div
    data-testid="tm-report-drilldown__view"
    class="tm-report-drilldown transaction-matching-consolidated-results transaction-matching-consolidated-results--v2"
  >
    <TMReportDrilldownToolbar
      :loading="loading"
      :summary-amount="summaryAmount"
      :total-count="totalCount"
      :page="page"
      :page-count="pageCount"
      :active-filter-count="activeFilterCount"
      :active-sort-count="activeSortCount"
      :download-history="downloadHistory"
      :loading-history="loadingHistory"
      :show-sort-modal="showSortModal"
      :active-sort-tab="activeSortTab"
      :available-sort-columns="availableSortColumns"
      :saved-sort="savedSort"
      :playground-sort="playgroundSort"
      :enable-saved-filter-sort="enableSavedFilterSort"
      @back="$emit('back')"
      @refresh="$emit('refresh')"
      @open-filter="openFilterModal"
      @open-sort="openSortModal"
      @close-sort="closeSortModal"
      @update:active-sort-tab="activeSortTab = $event"
      @toggle-sort-column="toggleSortColumn"
      @set-sort-direction="setSortDirection"
      @reset-sort="resetSort"
      @apply-sort="handleApplySort"
      @page-change="$emit('page-change', $event)"
      @generate-excel="handleGenerateExcel"
      @refresh-download-history="$emit('refresh-download-history')"
      @download-history-file="$emit('download-history-file', $event)"
    />

    <div
      v-if="loading && !records.length && !hasBuckets && !error"
      class="consolidated-results__loading tm-report-drilldown__loading"
      data-testid="tm-report-drilldown__loading"
    >
      <img
        src="/img/loader_new.svg"
        alt="Loading"
        class="consolidated-results__loading-image tm-report-drilldown__loading-img"
        data-testid="tm-report-drilldown__loading-image"
      />
    </div>

    <div
      v-else-if="error"
      class="text-center grey--text pa-6"
      :data-testid="error === 'not_configured'
        ? 'tm-report-drilldown__error-not-configured'
        : 'tm-report-drilldown__error'"
    >
      <template v-if="error === 'not_configured'">
        Drill-down is not configured for "{{ field }}".
      </template>
      <template v-else>
        Failed to load drill-down data. Use Refresh to try again.
      </template>
    </div>

    <template v-else-if="hasBuckets">
      <v-tabs v-model="activeBucketTab" class="tm-report-drilldown__bucket-tabs" show-arrows>
        <v-tab
          v-for="(bucket, index) in mappedBuckets"
          :key="bucket.name + index"
          :data-testid="'tm-report-drilldown__bucket-tab--' + index"
        >
          {{ bucket.name }}
        </v-tab>
      </v-tabs>

      <div
        v-for="(bucket, index) in mappedBuckets"
        v-show="activeBucketTab === index"
        :key="'bucket-content-' + bucket.name + index"
        class="tm-report-drilldown__bucket-content"
      >
        <TMResultsTableWrapper
          :table-headers="bucket.tableHeaders"
          :rows="bucket.records"
          :loading="loading"
          :draggable-headers="false"
          variant="v2"
          :applied-sort="appliedSort"
          :applied-filters="appliedFilters"
          :column-type-map="columnTypeMap"
        />
      </div>
    </template>

    <template v-else>
      <TMResultsTableWrapper
        :table-headers="tableHeaders"
        :rows="records"
        :loading="loading"
        :draggable-headers="false"
        variant="v2"
        :applied-sort="appliedSort"
        :applied-filters="appliedFilters"
        :column-type-map="columnTypeMap"
      />

      <div
        v-if="!loading && records.length === 0"
        class="text-center grey--text pa-6"
        data-testid="tm-report-drilldown__empty"
      >
        No drill-down records found for this field.
      </div>
    </template>

    <div
      v-if="showFilterModal"
      data-testid="tm-report-drilldown__filter-modal-overlay"
      class="consolidated-results__filter-modal-overlay"
      @click.self="closeFilterModal"
    >
      <div
        data-testid="tm-report-drilldown__filter-modal"
        class="consolidated-results__filter-modal"
      >
        <div class="consolidated-results__filter-modal-header">
          <h3 class="consolidated-results__filter-modal-title">Filter Drill-Down Data</h3>
          <button
            type="button"
            data-testid="tm-report-drilldown__filter-modal-close"
            class="consolidated-results__filter-modal-close"
            @click="closeFilterModal"
          >
            <svg class="consolidated-results__filter-modal-close-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div v-if="enableSavedFilterSort" class="consolidated-results__tabs">
          <button
            type="button"
            data-testid="tm-report-drilldown__filter-tab-saved"
            :class="['consolidated-results__tab', activeFilterTab === 'saved' ? 'consolidated-results__tab--active' : '']"
            @click="activeFilterTab = 'saved'"
          >
            Saved Filters
          </button>
          <button
            type="button"
            data-testid="tm-report-drilldown__filter-tab-playground"
            :class="['consolidated-results__tab', activeFilterTab === 'playground' ? 'consolidated-results__tab--active' : '']"
            @click="activeFilterTab = 'playground'"
          >
            Filter
          </button>
        </div>

        <div class="consolidated-results__filter-modal-body">
          <transition name="tab-slide" mode="out-in">
            <TMResultsFilterBuilder
              :key="effectiveFilterTab"
              test-id-prefix="tm-report-drilldown"
              :filter-groups="getCurrentFilterGroups()"
              :grouping-connector="getCurrentGroupingConnector()"
              :filter-columns="filterColumns"
              :filter-operators="filterOperators"
              :has-active-filters="(appliedFilters?.groups || []).length > 0"
              @set-grouping-connector="setCurrentGroupingConnector"
              @add-group="addGroup"
              @remove-group="removeGroup"
              @reset="resetFilters"
              @apply="applyFilters"
              @update-group-name="updateGroupName"
              @update-group-connector="updateGroupConnector"
              @update-condition="updateCondition"
              @add-condition="addCondition"
              @remove-condition="removeCondition"
            />
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TMReportDrilldownToolbar from './TMReportDrilldownToolbar.vue';
import TMResultsFilterBuilder from '../shared/TMResultsFilterBuilder.vue';
import TMResultsTableWrapper from '../shared/TMResultsTableWrapper.vue';
import {
  FILTER_OPERATORS,
  createDefaultFilterGroup,
  createDefaultCondition,
  buildFilterPayload,
  formatHeaderText,
  resolveExportDataFilters,
} from '../shared/tmResultsDataTableUtils.js';
import { mapBucketForDisplay, normalizeDefaultFilters } from './tmReportDrilldownViewUtils.js';

export default {
  name: 'TMReportDrilldownView',

  components: {
    TMReportDrilldownToolbar,
    TMResultsFilterBuilder,
    TMResultsTableWrapper,
  },

  props: {
    field: {
      type: String,
      default: '',
    },
    summaryAmount: {
      type: Number,
      default: null,
    },
    records: {
      type: Array,
      default: () => [],
    },
    tableHeaders: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageCount: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    activeFilterCount: {
      type: Number,
      default: 0,
    },
    activeSortCount: {
      type: Number,
      default: 0,
    },
    appliedFilters: {
      type: Object,
      default: () => ({ groups: [], group_connectors: [] }),
    },
    appliedSort: {
      type: Array,
      default: () => [],
    },
    defaultFilters: {
      type: Object,
      default: () => ({ groups: [], group_connectors: [] }),
    },
    defaultSort: {
      type: Array,
      default: () => [],
    },
    filterColumns: {
      type: Array,
      default: () => [],
    },
    downloadHistory: {
      type: Array,
      default: () => [],
    },
    loadingHistory: {
      type: Boolean,
      default: false,
    },
    buckets: {
      type: Array,
      default: null,
    },
    columnTypeMap: {
      type: Object,
      default: () => ({}),
    },
    error: {
      type: String,
      default: null,
    },
    /**
     * When false, hides Saved Filters / Saved Sort tabs and uses plain Filter / Sort only.
     */
    enableSavedFilterSort: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      showFilterModal: false,
      showSortModal: false,
      activeFilterTab: 'saved',
      savedFilters: { groups: [], group_connectors: [] },
      playgroundFilters: { groups: [], group_connectors: [] },
      modalFilterSnapshot: null,
      playgroundFilterGroupingConnector: 'OR',
      filterOperators: FILTER_OPERATORS,
      activeSortTab: 'saved',
      savedSort: [],
      playgroundSort: [],
      lastAppliedFilterTab: 'playground',
      activeBucketTab: 0,
    };
  },

  computed: {
    effectiveFilterTab() {
      return this.enableSavedFilterSort ? this.activeFilterTab : 'playground';
    },

    hasBuckets() {
      return Array.isArray(this.buckets) && this.buckets.length > 0;
    },

    mappedBuckets() {
      if (!this.hasBuckets) return [];
      return this.buckets.map((bucket) => mapBucketForDisplay(bucket, this.columnTypeMap));
    },

    availableSortColumns() {
      return (this.tableHeaders || []).map((header) => ({
        key: header.key,
        label: header.label || formatHeaderText(header.key),
      }));
    },
  },

  watch: {
    defaultFilters: {
      handler() {
        if (this.showFilterModal) {
          this.initializeFilterState();
        }
      },
      deep: true,
    },
    defaultSort: {
      handler(sort) {
        this.savedSort = Array.isArray(sort) ? JSON.parse(JSON.stringify(sort)) : [];
      },
      immediate: true,
      deep: true,
    },
    buckets() {
      this.activeBucketTab = 0;
    },
  },

  methods: {
    openFilterModal() {
      this.initializeFilterState();
      this.modalFilterSnapshot = {
        playgroundFilters: JSON.parse(JSON.stringify(this.playgroundFilters)),
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
        savedFilters: JSON.parse(JSON.stringify(this.savedFilters)),
      };
      this.showFilterModal = true;
    },

    closeFilterModal(restore = true) {
      this.showFilterModal = false;
      if (restore && this.modalFilterSnapshot) {
        this.playgroundFilters = JSON.parse(JSON.stringify(this.modalFilterSnapshot.playgroundFilters));
        this.playgroundFilterGroupingConnector = this.modalFilterSnapshot.playgroundFilterGroupingConnector;
        this.savedFilters = JSON.parse(JSON.stringify(this.modalFilterSnapshot.savedFilters));
      }
    },

    initializeFilterState() {
      const defaults = normalizeDefaultFilters(this.defaultFilters);
      this.savedFilters = defaults.groups.length
        ? JSON.parse(JSON.stringify(defaults))
        : this.createEmptyFilterGroup();

      if ((this.playgroundFilters?.groups?.length || 0) === 0 && (this.appliedFilters?.groups?.length || 0) > 0) {
        this.playgroundFilters = JSON.parse(JSON.stringify(this.appliedFilters));
        this.playgroundFilterGroupingConnector = this.appliedFilters.group_connectors?.[0] || 'OR';
      } else if (!this.playgroundFilters?.groups?.length) {
        this.playgroundFilters = this.createEmptyFilterGroup();
      }

      this.activeFilterTab = this.enableSavedFilterSort ? 'saved' : 'playground';
    },

    createEmptyFilterGroup() {
      return {
        groups: [createDefaultFilterGroup()],
        group_connectors: [],
      };
    },

    getCurrentFilterGroups() {
      return this.effectiveFilterTab === 'saved'
        ? this.savedFilters.groups
        : this.playgroundFilters.groups;
    },

    getCurrentGroupingConnector() {
      if (this.effectiveFilterTab === 'saved') {
        return (this.savedFilters.group_connectors && this.savedFilters.group_connectors[0]) || 'OR';
      }
      return this.playgroundFilterGroupingConnector;
    },

    setCurrentGroupingConnector(connector) {
      if (this.effectiveFilterTab === 'saved') {
        if (!this.savedFilters.group_connectors) {
          this.$set(this.savedFilters, 'group_connectors', []);
        }
        this.$set(this.savedFilters.group_connectors, 0, connector);
      } else {
        this.playgroundFilterGroupingConnector = connector;
      }
    },

    addGroup() {
      this.getCurrentFilterGroups().push(createDefaultFilterGroup());
    },

    removeGroup(groupId) {
      const currentGroups = this.getCurrentFilterGroups();
      if (currentGroups.length === 1) {
        this.resetFilters();
        return;
      }
      const newGroups = currentGroups.filter((g) => g.id !== groupId);
      if (this.effectiveFilterTab === 'saved') {
        this.savedFilters.groups = newGroups;
      } else {
        this.playgroundFilters.groups = newGroups;
      }
    },

    resetFilters() {
      const emptyGroup = this.createEmptyFilterGroup();
      if (this.effectiveFilterTab === 'saved') {
        this.savedFilters = emptyGroup;
      } else {
        this.playgroundFilters = emptyGroup;
        this.playgroundFilterGroupingConnector = 'OR';
      }
    },

    addCondition(groupId) {
      const group = this.getCurrentFilterGroups().find((g) => g.id === groupId);
      if (group) {
        group.conditions.push(createDefaultCondition());
      }
    },

    removeCondition(groupId, conditionId) {
      const group = this.getCurrentFilterGroups().find((g) => g.id === groupId);
      if (!group) return;
      const remaining = group.conditions.filter((c) => c.id !== conditionId);
      group.conditions = remaining.length > 0 ? remaining : [createDefaultCondition()];
    },

    updateCondition(groupId, conditionId, field, value) {
      const group = this.getCurrentFilterGroups().find((g) => g.id === groupId);
      if (!group) return;
      const condition = group.conditions.find((c) => c.id === conditionId);
      if (condition) {
        this.$set(condition, field, value);
      }
    },

    updateGroupConnector(groupId, connector) {
      const group = this.getCurrentFilterGroups().find((g) => g.id === groupId);
      if (group) {
        this.$set(group, 'connector', connector);
      }
    },

    updateGroupName(groupId, name) {
      const group = this.getCurrentFilterGroups().find((g) => g.id === groupId);
      if (group) {
        this.$set(group, 'name', name);
      }
    },

    applyFilters() {
      const groups = this.effectiveFilterTab === 'saved'
        ? this.savedFilters.groups || []
        : this.playgroundFilters.groups || [];
      const connector = this.getCurrentGroupingConnector();
      const payload = buildFilterPayload(groups, connector, this.columnTypeMap);
      this.lastAppliedFilterTab = this.effectiveFilterTab;
      this.modalFilterSnapshot = {
        playgroundFilters: JSON.parse(JSON.stringify(this.playgroundFilters)),
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
        savedFilters: JSON.parse(JSON.stringify(this.savedFilters)),
      };
      this.closeFilterModal(false);
      this.$emit('apply-filters', payload);
    },

    openSortModal() {
      if (this.playgroundSort.length === 0 && this.appliedSort.length > 0) {
        this.playgroundSort = JSON.parse(JSON.stringify(this.appliedSort));
      }
      this.activeSortTab = this.enableSavedFilterSort ? 'saved' : 'playground';
      this.showSortModal = true;
    },

    closeSortModal() {
      this.showSortModal = false;
    },

    getCurrentSortItems() {
      const tab = this.enableSavedFilterSort ? this.activeSortTab : 'playground';
      const items = tab === 'saved' ? this.savedSort : this.playgroundSort;
      return Array.isArray(items) ? items : [];
    },

    toggleSortColumn(columnKey) {
      const currentSort = [...this.getCurrentSortItems()];
      const index = currentSort.findIndex((item) => item.key === columnKey);
      if (index >= 0) {
        currentSort.splice(index, 1);
      } else {
        currentSort.push({ key: columnKey, direction: 'asc' });
      }
      const tab = this.enableSavedFilterSort ? this.activeSortTab : 'playground';
      if (tab === 'saved') {
        this.savedSort = currentSort;
      } else {
        this.playgroundSort = currentSort;
      }
    },

    setSortDirection(columnKey, direction) {
      const currentSort = this.getCurrentSortItems().map((item) => (
        item.key === columnKey ? { ...item, direction } : item
      ));
      const tab = this.enableSavedFilterSort ? this.activeSortTab : 'playground';
      if (tab === 'saved') {
        this.savedSort = currentSort;
      } else {
        this.playgroundSort = currentSort;
      }
    },

    resetSort() {
      const tab = this.enableSavedFilterSort ? this.activeSortTab : 'playground';
      if (tab === 'saved') {
        this.savedSort = [];
      } else {
        this.playgroundSort = [];
      }
    },

    handleApplySort() {
      const tab = this.enableSavedFilterSort ? this.activeSortTab : 'playground';
      const nextSort = tab === 'saved'
        ? JSON.parse(JSON.stringify(this.savedSort))
        : JSON.parse(JSON.stringify(this.playgroundSort));
      this.closeSortModal();
      this.$emit('apply-sort', nextSort);
    },

    /**
     * Resolve export filters and sort using active-tab filter priority (playground → applied → saved),
     * falling back to API filters from the last drill-down fetch.
     * @param {{ apiExportFilters?: Object|null }} options
     * @returns {{ filters: Object|null, sort: Array|null }}
     */
    getFiltersAndSortForExport({ apiExportFilters = null } = {}) {
      return resolveExportDataFilters({
        lastAppliedFilterTab: this.lastAppliedFilterTab,
        playgroundFilters: this.playgroundFilters,
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
        appliedFilters: this.appliedFilters,
        savedFilters: this.savedFilters,
        playgroundSort: this.playgroundSort,
        appliedSort: this.appliedSort,
        savedSort: this.savedSort,
        columnTypeMap: this.columnTypeMap,
        apiExportFilters,
        enableSavedFilterSort: this.enableSavedFilterSort,
      });
    },

    handleGenerateExcel() {
      this.$emit('generate-result');
    },
  },
};
</script>

<style lang="scss">
@import '../shared/TMResultsDataTable.scss';
</style>

<style lang="scss" scoped>
.tm-report-drilldown {
  width: 100%;
}

.tm-report-drilldown__loading {
  display: flex;
  justify-content: center;
  padding: var(--space-8);
}

.tm-report-drilldown__loading-img {
  width: 3rem;
  height: 3rem;
}

.tm-report-drilldown__bucket-tabs {
  margin-top: var(--space-3);
}

.tm-report-drilldown__bucket-content {
  margin-top: var(--space-3);
}
</style>
